import { env } from '@/app/config/env';
import { apiClient } from '@/shared/services/api/client';
import { unwrapEnvelope } from '@/shared/services/api/envelope';
import type { ApiEnvelope } from '@/shared/services/api/envelope';
import type { CatalogKind, CatalogOption, PurchaseRequestUsage } from '../models/templateForm.types';
import { mockActiveTemplateListing, mockPurchaseRequestTemplate } from '@/features/templates';
import {
  mockDeactivatePurchaseRequest,
  mockDecideSubmission,
  mockGetSubmission,
  mockListSubmissions,
  mockPurchaseRequestUsage,
  mockPurchaseRequestDeleteImpact,
  mockExportSubmissions,
  mockImportPurchaseRequests,
  mockImportTemplate,
  mockReservePurchaseRequestNumber,
  mockSubmissionSummary,
  mockSubmitTemplateForm,
  mockUpdatePurchaseRequest,
} from '../__mocks__/templateForms.mock';
import { mockCatalogOptions } from '../__mocks__/purchaseCatalog.mock';
import { createFormsApi } from './formsApi';
import type { FormsApi } from './formsApi';

/**
 * Purchase Requests (NB-355), `/purchase-requests`: the forms contract for PR Templates --
 * `GET /templates` the active PR Templates mapped to the selected hierarchy (an Initiator's: those mapped to their
 * own), `GET /templates/{id}` one with its mapping, `GET /` the requests, `GET /{id}` one,
 * `POST /` a submit, `PUT /{id}` an edit, `DELETE /{id}` a soft delete (Inactive), `POST /{id}/decisions` a decision --
 * and the list's `GET /summary` cards, `GET /export`, `POST /import` and `GET /import-template`.
 */
export const purchaseRequestApi: FormsApi = createFormsApi({
  templatesPath: '/purchase-requests/templates',
  /* An Initiator holds no PR Template grant: the template they use is read here, not from /templates. */
  templateDetail: true,
  submissionsPath: '/purchase-requests',
  summaryPath: '/purchase-requests/summary',
  transfers: true,
  nextNumberPath: '/purchase-requests/next-number',
  edits: true,
  noun: { one: 'purchase request', many: 'purchase requests', submit: 'purchase request' },
  mocks: env.ENABLE_MOCKS
    ? {
        listActiveTemplates: (query) => mockActiveTemplateListing(query, 'PURCHASE_REQUEST'),
        getTemplate: (id) => mockPurchaseRequestTemplate(id),
        listSubmissions: (query) => mockListSubmissions(query),
        getSubmission: (id) => mockGetSubmission(id),
        submit: (payload) => mockSubmitTemplateForm(payload),
        decide: (id, decision, comment, step) => mockDecideSubmission(id, decision, comment, step),
        summary: (query) => mockSubmissionSummary(query),
        exportList: (request) => mockExportSubmissions(request),
        importFile: (file) => mockImportPurchaseRequests(file),
        importTemplate: () => Promise.resolve(mockImportTemplate()),
        nextNumber: () => mockReservePurchaseRequestNumber(),
        update: (id, payload) => mockUpdatePurchaseRequest(id, payload),
        remove: (id) => mockDeactivatePurchaseRequest(id),
        deleteImpact: (id) => mockPurchaseRequestDeleteImpact(id),
      }
    : null,
});

/* A refusal is the screen's news, not grounds to throw the user out of the shell. */
const allowForbidden = { _allowForbidden: true } as never;

const one = (id: number) => `/purchase-requests/${String(id)}`;

/** What refers to a saved request -- `GET /{id}/usage`: its BIDs, and Purchase Orders (0 until they exist). */
export async function getPurchaseRequestUsage(id: number): Promise<PurchaseRequestUsage> {
  if (env.ENABLE_MOCKS) return mockPurchaseRequestUsage(id);
  return unwrapEnvelope(
    await apiClient.get<ApiEnvelope<PurchaseRequestUsage>>(`${one(id)}/usage`, allowForbidden),
    'Unable to load the usage. Please try again.',
  );
}

/** What a line-item dropdown is read for: the request's entity, and for products the line's location and category. */
export interface CatalogQuery {
  legalEntityIds: readonly number[];
  locationId?: number | null;
  categoryId?: number | null;
  /** For categories: the request's PR Template, whose categories alone are offered (2026-10-09). */
  templateId?: number | null;
  /**
   * For categories: an edited request's copy of its template's categories, offered instead of the template's
   * categories now. Takes precedence over `templateId`.
   */
  categoryIds?: readonly number[] | null;
}

/**
 * A line item's Location, Category or Product options -- `GET /purchase-requests/line-item-options/{kind}`: Active
 * rows of the request's entity, labelled `CODE - Name`; categories those of the request's PR Template (`templateId`);
 * products are those of the category linked to the location, so none are asked for until both are chosen.
 */
export async function listCatalogOptions(kind: CatalogKind, query: CatalogQuery): Promise<CatalogOption[]> {
  if (kind === 'products' && (query.locationId == null || query.categoryId == null)) return [];
  if (env.ENABLE_MOCKS) return mockCatalogOptions(kind, query);

  const params: Record<string, string | number> = {};
  if (query.legalEntityIds.length > 0) params.legalEntityIds = query.legalEntityIds.join(',');
  if (query.locationId != null) params.locationId = query.locationId;
  if (query.categoryId != null) params.categoryId = query.categoryId;
  if (kind === 'categories' && query.categoryIds != null && query.categoryIds.length > 0) {
    params.categoryIds = query.categoryIds.join(',');
  } else if (kind === 'categories' && query.templateId != null) params.templateId = query.templateId;
  return unwrapEnvelope(
    await apiClient.get<ApiEnvelope<CatalogOption[]>>(`/purchase-requests/line-item-options/${kind}`, {
      _allowForbidden: true,
      params,
    } as never),
    'Unable to load the line item options. Please try again.',
  );
}
